import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Mail';
import { api, apiErrorMessage } from '../api/client';
import { track } from '../lib/analytics';

interface Props {
  compact?: boolean;
}

const ROLES = [
  { value: '', label: "I'm a…" },
  { value: 'coach', label: 'Coach' },
  { value: 'admin', label: 'Athletic director / admin' },
  { value: 'parent', label: 'Parent' },
  { value: 'athlete', label: 'Athlete' },
  { value: 'other', label: 'Other' },
];

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'] as const;
const UTM_STORAGE_KEY = 'tf.utm_attribution';

function readUTM(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};

  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) out[k] = v;
  }

  if (Object.keys(out).length === 0) {
    try {
      const stored = window.sessionStorage.getItem(UTM_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, string>;
        for (const k of UTM_KEYS) {
          if (parsed[k]) out[k] = parsed[k];
        }
      }
    } catch {
      // sessionStorage may be disabled — fall through silently.
    }
  }

  const source = params.get('source') || document.referrer;
  if (source) out.source = source.slice(0, 80);
  return out;
}

export default function WaitlistForm({ compact = false }: Props) {
  const [first, setFirst] = useState('');
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [org, setOrg] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const startedRef = useRef(false);

  function handleStart() {
    if (startedRef.current) return;
    startedRef.current = true;
    track('Waitlist Started', { variant: compact ? 'compact' : 'full' });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email.');
      track('Waitlist Error', { reason: 'missing_email' });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/waitlist', {
        first_name: first,
        email,
        role,
        org_name: org,
        website,
        ...readUTM(),
      });
      setDone(true);
      track('Waitlist Submitted', {
        role: role || 'unspecified',
        has_org: org.trim() ? 'yes' : 'no',
        variant: compact ? 'compact' : 'full',
      });
      setFirst('');
      setEmail('');
      setRole('');
      setOrg('');
    } catch (err) {
      const message = apiErrorMessage(err, 'Could not sign up. Please try again.');
      setError(message);
      track('Waitlist Error', { reason: 'api_error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      id="waitlist"
      sx={{
        scrollMarginTop: 96,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
      }}
      elevation={0}
    >
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        {!compact && (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
              Get early access
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              We're onboarding 50 founding coaches free for life. Tell us where you coach and
              we'll email you when it's your turn.
            </Typography>
          </Box>
        )}

        {done ? (
          <Alert severity="success" icon={<EmailIcon fontSize="inherit" />}>
            You're in. Check your inbox — your one-click founding-coach signup link is on the way.
            (Reply and tell us your most painful team-admin chore while you're there.)
          </Alert>
        ) : (
          <form onSubmit={onSubmit} onFocus={handleStart} aria-label="Join the waitlist">
            <Grid container spacing={1.5}>
              {error && (
                <Grid item xs={12}>
                  <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First name"
                  value={first}
                  onChange={(e) => setFirst(e.target.value)}
                  autoComplete="given-name"
                  inputProps={{ 'aria-label': 'First name' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Role"
                  select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  inputProps={{ 'aria-label': 'Your role' }}
                >
                  {ROLES.map((r) => (
                    <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="coach@yourschool.org"
                  inputProps={{ 'aria-label': 'Email address' }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="School / club (optional)"
                  value={org}
                  onChange={(e) => setOrg(e.target.value)}
                  autoComplete="organization"
                  placeholder="Roadrunners Track Club"
                  inputProps={{ 'aria-label': 'School or club' }}
                />
              </Grid>
              {/* Honeypot — visually hidden, irresistible to bots. */}
              <Box
                component="label"
                aria-hidden
                sx={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
              >
                Website
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </Box>
              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={1.5}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={busy}
                    aria-label="Join the waitlist"
                    sx={{ px: 3, py: 1.1 }}
                  >
                    {busy ? 'Sending…' : 'Join the waitlist'}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    No spam. Unsubscribe in one click.
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
